import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";

/**
 * Cookie consent banner — CASL + ePrivacy compliant.
 *
 * Persists the user's decision in localStorage under
 * `lh.cookie.consent.v1`. Until "Accept all" or "Manage preferences →
 * Analytics on" is chosen, NO non-essential tracking fires — in
 * particular the Cloudflare RUM beacon at `/cdn-cgi/rum` is suppressed
 * via the `window.__lh_block_rum` flag read by index.html.
 *
 * Strictly necessary cookies (session, CSRF, theme) are always on and
 * not subject to consent — that follows ePrivacy convention.
 *
 * Bump CONSENT_VERSION when categories or wording change to force a
 * re-prompt.
 */
const CONSENT_VERSION = 1;
const STORAGE_KEY = `lh.cookie.consent.v${CONSENT_VERSION}`;

const DEFAULT_PREFS = {
  necessary: true,    // always on
  analytics: false,   // off until opt-in
  marketing: false,   // reserved, currently unused
};

function readPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return null;
  }
}

function writePrefs(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch { /* localStorage full / blocked — accept the choice in-memory only */ }
  // Expose globally so the index.html RUM injector can read it.
  window.__lh_cookie_prefs = prefs;
}

/** Public API: components or analytics loaders can read this. */
export function getConsent() {
  return readPrefs() || DEFAULT_PREFS;
}

export function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = readPrefs();
    if (existing) {
      // Already chose — make sure the global flag is up to date.
      window.__lh_cookie_prefs = existing;
      // Respect Do-Not-Track at the analytics layer.
      if (navigator.doNotTrack === "1") {
        window.__lh_cookie_prefs = { ...existing, analytics: false };
      }
    } else {
      setOpen(true);
    }
    // Listen for the "Cookie prefs" footer link.
    const handler = () => { setOpen(true); setShowPrefs(true); };
    window.addEventListener("lh:cookie-prefs:open", handler);
    return () => window.removeEventListener("lh:cookie-prefs:open", handler);
  }, []);

  const acceptAll = useCallback(() => {
    const prefs = { necessary: true, analytics: true, marketing: true };
    writePrefs(prefs);
    setOpen(false);
  }, []);

  const necessaryOnly = useCallback(() => {
    const prefs = { necessary: true, analytics: false, marketing: false };
    writePrefs(prefs);
    setOpen(false);
  }, []);

  const savePrefs = useCallback(() => {
    const prefs = {
      necessary: true,
      analytics,
      marketing,
    };
    writePrefs(prefs);
    setOpen(false);
    setShowPrefs(false);
  }, [analytics, marketing]);

  if (!open) return null;

  return (
    <div
      data-testid="cookie-consent"
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className="fixed bottom-3 left-3 right-3 sm:left-auto sm:max-w-md sm:right-3 z-[120] rounded-2xl border border-[#14b8a6]/40 bg-black/85 backdrop-blur-md shadow-[0_0_40px_-15px_rgba(20,184,166,0.5)] p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <i className="fas fa-cookie-bite text-[#14b8a6] text-xl mt-0.5" />
        <div className="flex-1">
          <p className="text-[12px] tracking-wide text-white/85 font-bold">
            Cookies on LEAGUE HUB
          </p>
          <p className="mt-1 text-[12px] text-white/65 leading-snug">
            We use strictly-necessary cookies to keep you signed in and
            the site working. With your consent we also use{" "}
            <strong>Cloudflare Analytics</strong> to measure performance.
            See our{" "}
            <Link to="/legal/cookies" className="underline text-[#14b8a6]">
              Cookie Policy
            </Link>{" "}
            for details.
          </p>

          {showPrefs && (
            <div className="mt-3 space-y-2 text-[11px]">
              <label className="flex items-center gap-2 opacity-60 cursor-not-allowed">
                <input type="checkbox" checked readOnly className="accent-[#14b8a6]" />
                <span>Strictly necessary — always on</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  data-testid="cookie-consent-analytics-toggle"
                  className="accent-[#14b8a6]"
                />
                <span>Analytics — Cloudflare RUM (page-load timing)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  data-testid="cookie-consent-marketing-toggle"
                  className="accent-[#14b8a6]"
                />
                <span>Marketing — reserved for future use</span>
              </label>
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2 text-[10px] tracking-widest">
            {!showPrefs ? (
              <>
                <button
                  type="button"
                  onClick={necessaryOnly}
                  data-testid="cookie-consent-necessary-only"
                  className="px-3 py-1.5 rounded-lg bg-white/8 hover:bg-white/15 text-white"
                >
                  NECESSARY ONLY
                </button>
                <button
                  type="button"
                  onClick={() => setShowPrefs(true)}
                  data-testid="cookie-consent-manage"
                  className="px-3 py-1.5 rounded-lg bg-white/8 hover:bg-white/15 text-white"
                >
                  MANAGE PREFERENCES
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  data-testid="cookie-consent-accept-all"
                  className="px-3 py-1.5 rounded-lg bg-[#14b8a6] text-black font-bold hover:bg-[#10a39a]"
                >
                  ACCEPT ALL
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setShowPrefs(false)}
                  className="px-3 py-1.5 rounded-lg bg-white/8 hover:bg-white/15 text-white"
                >
                  BACK
                </button>
                <button
                  type="button"
                  onClick={savePrefs}
                  data-testid="cookie-consent-save"
                  className="px-3 py-1.5 rounded-lg bg-[#14b8a6] text-black font-bold hover:bg-[#10a39a]"
                >
                  SAVE PREFERENCES
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
